/**
 * Monthly check-ins, dose and stack requests, and Dr. Hinds' answer.
 *
 * One presentational component, mounted on the support chart (admin
 * "Check-ins & dose changes" tab), in the clinician PatientChartDrawer, and
 * as a compact strip in the PrescribingWorkspace order review. Callers fetch
 * the data from their own authorized endpoint. A renewal approved without an
 * answer shows "Decision not recorded": the backend never infers a reason.
 */
import { Fragment, useState, type ReactNode } from "react";

import { Badge } from "@/design-system/primitives";
import type {
  CheckinAskedFor,
  CheckinDecision,
  CheckinDoseHistoryResponse,
  CheckinDoseLimit,
  CheckinHistoryRow,
  CheckinMedicationCard,
  CheckinSource,
  CheckinWeightPoint,
  CheckinWeightSummary,
} from "@/types/checkinDoseHistory";

type Tone = "neutral" | "success" | "warning" | "danger" | "info";

export const ASKED_FOR_LABEL: Record<CheckinAskedFor, string> = {
  increase_dose: "Increase dose",
  decrease_dose: "Decrease dose",
  discuss_dose: "Discuss dose",
  change_dose: "Change dose",
  add_to_stack: "Add to stack",
  no_change: "No change",
  start_treatment: "Start treatment",
};

const SOURCE_LABEL: Record<CheckinSource, string> = {
  monthly_checkin: "Monthly check-in",
  renewal_intake: "Renewal intake",
  message: "Message to care team",
  care_team: "Care team note",
  first_intake: "First intake",
  renewal: "Renewal",
};

export function decisionLabel(row: Pick<CheckinHistoryRow, "decision" | "dose_after">): {
  label: string;
  tone: Tone;
} {
  const dose = row.dose_after;
  const map: Record<CheckinDecision, { label: string; tone: Tone }> = {
    approved: { label: "Approved", tone: "success" },
    increased: { label: dose ? `Increased to ${dose}` : "Increased", tone: "success" },
    changed: { label: dose ? `Changed to ${dose}` : "Changed", tone: "success" },
    directions_changed: { label: "Directions changed", tone: "success" },
    kept_current_dose: { label: "Kept current dose", tone: "warning" },
    closed_no_change: { label: "Closed, no dose change", tone: "neutral" },
    cleared: { label: "Cleared", tone: "neutral" },
    visit_requested: { label: "Visit requested", tone: "info" },
    pending: { label: "Pending", tone: "info" },
    renewed: { label: "Renewed", tone: "neutral" },
    not_recorded: { label: "Decision not recorded", tone: "neutral" },
    declined: { label: "Declined", tone: "danger" },
    cancelled: { label: "Cancelled", tone: "neutral" },
    no_decision_needed: { label: "No change requested", tone: "neutral" },
  };
  return map[row.decision];
}

function day(value: string | null | undefined): string {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "—"
    : date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function doseLimitText(limit: CheckinDoseLimit): string {
  const per = `${limit.container_ml_per_cycle} mL per 28 days`;
  return limit.max_units != null
    ? `Up to ${limit.max_units} units · ${limit.injection_days ?? "?"} injection days · ${per}`
    : per;
}

function Fact({ label, value, hint }: { label: string; value: ReactNode; hint?: ReactNode }) {
  return (
    <div className="flex flex-col gap-2 min-w-0">
      <span className="text-11 text-ink-muted">{label}</span>
      <span className="type-body-sm font-medium break-words">{value}</span>
      {hint && <span className="text-11 text-ink-muted break-words">{hint}</span>}
    </div>
  );
}

const BMI_FLAG: Record<"below_25" | "25_to_27", { label: string; tone: Tone }> = {
  below_25: { label: "Below 25", tone: "danger" },
  "25_to_27": { label: "25–27", tone: "warning" },
};

function pounds(value: number): string {
  return `${Number.isInteger(value) ? value : value.toFixed(1)} lb`;
}

function shortDay(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "—"
    : date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

/** Latest weight with its change since the first GLP-1 intake, and the BMI
 * flagged the way the order-review card flags it (red below 25, amber 25–27). */
function WeightFacts({ summary }: { summary: CheckinWeightSummary }) {
  const change = summary.change_lb;
  const flag = summary.bmi?.flag ? BMI_FLAG[summary.bmi.flag] : null;
  return (
    <>
      <Fact
        label="Weight"
        value={`${pounds(summary.latest_lb)} · ${shortDay(summary.latest_at)}`}
        hint={`${change > 0 ? "+" : change < 0 ? "−" : "±"}${pounds(Math.abs(change))} since start (${pounds(summary.starting_lb)})`}
      />
      <Fact
        label="BMI"
        value={
          summary.bmi ? (
            <span className="inline-flex items-center gap-6">
              {summary.bmi.value.toFixed(1)}
              {flag && (
                <Badge tone={flag.tone} size="sm">
                  {flag.label}
                </Badge>
              )}
            </span>
          ) : (
            "—"
          )
        }
        hint={
          summary.bmi?.height_from
            ? `height from ${summary.bmi.height_from}${summary.bmi.height_at ? `, ${shortDay(summary.bmi.height_at)}` : ""}`
            : undefined
        }
      />
    </>
  );
}

function WeightSeries({ points }: { points: CheckinWeightPoint[] }) {
  if (points.length < 2) return null;
  return (
    <ol aria-label="Weight at each check-in" className="flex flex-wrap gap-x-10 gap-y-2 text-11 text-ink-muted">
      {points.map((point) => (
        <li key={`${point.date}-${point.source}`}>
          {shortDay(point.date)} <span className="text-ink font-medium">{pounds(point.weight_lb)}</span>
          {point.source === "intake" ? " (intake)" : ""}
        </li>
      ))}
    </ol>
  );
}

function MedicationCard({ card }: { card: CheckinMedicationCard }) {
  return (
    <section className="border border-line-subtle p-10 flex flex-col gap-8">
      <div className="flex items-center justify-between gap-8">
        <h4 className="type-label-sm font-medium">{card.medication}</h4>
        {card.open_request ? (
          <Badge tone="info" size="sm">
            {ASKED_FOR_LABEL[card.open_request]} · Pending
          </Badge>
        ) : (
          <Badge tone="neutral" size="sm">
            No open requests
          </Badge>
        )}
      </div>
      <div className="grid grid-cols-3 gap-8">
        <Fact
          label="Current dose"
          value={card.current_dose ?? "—"}
          hint={card.since ? `since ${day(card.since)}` : undefined}
        />
        {card.has_monthly_checkin ? (
          <Fact label="Last check-in" value={day(card.last_checkin_at)} />
        ) : (
          <Fact
            label="Monthly check-in"
            value="None for this treatment"
            hint="Intake re-asked when a new Rx is needed"
          />
        )}
        {card.dose_limit ? (
          <Fact label="Dose limit" value={doseLimitText(card.dose_limit)} />
        ) : (
          <Fact label="Next renewal" value={day(card.next_renewal_at)} />
        )}
      </div>
      {card.weight_summary && (
        <div className="flex flex-col gap-6 border-t border-line-subtle pt-8">
          <div className="grid grid-cols-3 gap-8">
            <WeightFacts summary={card.weight_summary} />
          </div>
          <WeightSeries points={card.weights ?? []} />
        </div>
      )}
    </section>
  );
}

function CheckinDetails({ row, openCheckin }: { row: CheckinHistoryRow; openCheckin?: ReactNode }) {
  const checkin = row.checkin;
  if (!checkin) return null;
  const fields: { label: string; value: string | null | undefined }[] = [
    {
      label: "Weight",
      value: checkin.weight
        ? `${checkin.weight} lb${checkin.starting_weight ? ` (${checkin.starting_weight} at start)` : ""}`
        : null,
    },
    { label: "Side effects", value: checkin.side_effects },
    { label: "Stomach symptoms", value: checkin.gi_score ? `${checkin.gi_score} / 5` : null },
    { label: "Appetite", value: checkin.appetite },
    { label: "Satisfaction", value: checkin.satisfaction },
  ];
  const shown = fields.filter((field) => field.value);
  return (
    <div className="bg-sunken p-9 flex flex-col gap-7">
      <span className="type-label-xs font-medium">
        {checkin.form_name ?? "Check-in"} · {day(row.date)}
      </span>
      {shown.length ? (
        <dl className="grid grid-cols-2 md:grid-cols-5 gap-8">
          {shown.map((field) => (
            <div key={field.label}>
              <dt className="text-11 text-ink-muted">{field.label}</dt>
              <dd className="type-body-sm">{field.value}</dd>
            </div>
          ))}
        </dl>
      ) : (
        <span className="text-11 text-ink-muted">No summary fields on this form.</span>
      )}
      {openCheckin}
    </div>
  );
}

/** The full section: medication cards and the newest-first history. */
export function CheckinDoseHistoryView({
  data,
  openCheckin,
}: {
  data: CheckinDoseHistoryResponse;
  /** Link or button to the full submission, rendered under an opened check-in. */
  openCheckin?: ReactNode;
}) {
  const [open, setOpen] = useState<string | null>(null);
  return (
    <div className="flex flex-col gap-12">
      {data.checkins_unavailable && (
        <p className="text-11 text-ink-muted">
          Check-in answers couldn’t be read from the chart right now; orders and requests are still
          shown.
        </p>
      )}
      {data.medications.length > 0 && (
        <div className="grid gap-8 grid-cols-[repeat(auto-fit,minmax(min(24rem,100%),1fr))]">
          {data.medications.map((card) => (
            <MedicationCard key={card.family} card={card} />
          ))}
        </div>
      )}
      <section className="border border-line-subtle">
        <div className="px-10 py-8 bg-sunken border-b border-line-subtle flex items-center justify-between gap-8">
          <h4 className="type-label-sm font-medium">History</h4>
          <span className="text-11 text-ink-muted">Check-ins and change requests, newest first</span>
        </div>
        {data.history.length === 0 ? (
          <p className="p-10 type-body-sm text-ink-muted">No check-ins or dose requests yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full type-body-sm">
              <thead className="text-left text-11 text-ink-muted">
                <tr>
                  <th className="px-10 py-6 font-medium">Date</th>
                  <th className="px-10 py-6 font-medium">Medication</th>
                  <th className="px-10 py-6 font-medium">Patient asked for</th>
                  <th className="px-10 py-6 font-medium">Decision</th>
                  <th className="px-10 py-6 font-medium">Dose after</th>
                  <th className="px-10 py-6 font-medium">Reason</th>
                </tr>
              </thead>
              <tbody>
                {data.history.map((row, index) => {
                  const key = `${row.family}-${row.date}-${index}`;
                  const decision = decisionLabel(row);
                  const expanded = open === key;
                  return (
                    <Fragment key={key}>
                      <tr className="border-t border-line-subtle align-top">
                        <td className="px-10 py-7">
                          <div className="font-medium">{day(row.date)}</div>
                          <div className="text-11 text-ink-muted">{SOURCE_LABEL[row.source]}</div>
                        </td>
                        <td className="px-10 py-7">{row.medication}</td>
                        <td className="px-10 py-7">
                          <div>{ASKED_FOR_LABEL[row.asked_for]}</div>
                          {row.checkin && (
                            <button
                              type="button"
                              aria-expanded={expanded}
                              onClick={() => setOpen(expanded ? null : key)}
                              className="text-11 text-ink-muted underline"
                            >
                              {expanded ? "Hide check-in" : "Show check-in"}
                            </button>
                          )}
                        </td>
                        <td className="px-10 py-7">
                          <Badge tone={decision.tone} size="sm">
                            {decision.label}
                          </Badge>
                          {row.decided_at && (
                            <div className="text-11 text-ink-muted mt-2">
                              {day(row.decided_at)}
                              {row.decided_by ? ` · ${row.decided_by}` : ""}
                            </div>
                          )}
                        </td>
                        <td className="px-10 py-7">{row.dose_after ?? "—"}</td>
                        <td className="px-10 py-7 text-ink-muted">{row.reason ?? "—"}</td>
                      </tr>
                      {expanded && (
                        <tr>
                          <td colSpan={6} className="px-10 pb-10">
                            <CheckinDetails row={row} openCheckin={openCheckin} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

/** Compact strip for one treatment, next to the approve flow. */
export function CheckinDoseStrip({
  data,
  family,
  onOpenHistory,
}: {
  data: CheckinDoseHistoryResponse;
  family: string;
  onOpenHistory: () => void;
}) {
  const card = data.medications.find((item) => item.family === family);
  const rows = data.history.filter((row) => row.family === family).slice(0, 3);
  if (!card && rows.length === 0) return null;
  return (
    <section aria-label="Check-ins and dose changes" className="border border-line-subtle">
      <div className="px-10 py-8 bg-sunken border-b border-line-subtle flex items-center justify-between gap-8">
        <h3 className="type-label-sm font-medium">Check-ins &amp; dose changes</h3>
        <button type="button" onClick={onOpenHistory} className="text-11 underline">
          Open full history
        </button>
      </div>
      <div className="p-10 flex flex-col gap-8">
        {card && (
          <div className="grid grid-cols-2 gap-8">
            <Fact label="Current dose" value={card.current_dose ?? "—"} />
            {card.has_monthly_checkin ? (
              <Fact label="Last check-in" value={day(card.last_checkin_at)} />
            ) : (
              <Fact
                label="Dose limit"
                value={card.dose_limit ? doseLimitText(card.dose_limit) : "No monthly check-in"}
              />
            )}
            {card.weight_summary && <WeightFacts summary={card.weight_summary} />}
          </div>
        )}
        {rows.length > 0 && (
          <ul className="flex flex-col gap-5">
            {rows.map((row, index) => {
              const decision = decisionLabel(row);
              return (
                <li key={`${row.date}-${index}`} className="flex flex-wrap items-center gap-6 type-body-sm">
                  <span className="text-11 text-ink-muted w-[6.5rem] shrink-0">{day(row.date)}</span>
                  <span>{ASKED_FOR_LABEL[row.asked_for]}</span>
                  <Badge tone={decision.tone} size="sm">
                    {decision.label}
                  </Badge>
                  {row.reason && <span className="text-11 text-ink-muted">{row.reason}</span>}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}
